import {
  eachDayOfInterval,
  isSameDay,
  eachMonthOfInterval,
  isSameMonth,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  addYears,
  subYears,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subDays,
  areIntervalsOverlapping,
  isWithinInterval,
  endOfDay,
  isAfter,
} from 'date-fns';
import { DateFormats, formatDateRange, localFormat } from 'src/helpers/date-helpers';
import {
  AggregationPeriodEnum,
  IAggregatedValue,
  IDeviceBindingDto,
  QualityMetric,
  SummaryCalculatorEnum,
} from 'src/services/api/api-client';
import { DateRangeEnum } from './useTrendModal.t';
import i18next from 'i18next';
import { BarAppearanceType } from 'uikit/appBarChart/AppBarChart.component';

export const getFilledInterval = (dateRange: DateRangeEnum) =>
  ((
    {
      [DateRangeEnum.Week]: {
        getPointFunc: eachDayOfInterval,
        findValueFunc: (data, baseDate, subCount) => data.find((d) => isSameDay(d.from, subDays(baseDate, subCount))),
        format: DateFormats.shortDayOfWeek,
      },
      [DateRangeEnum.Month]: {
        getPointFunc: eachDayOfInterval,
        findValueFunc: (data, baseDate, subCount) => data.find((d) => isSameDay(d.from, subDays(baseDate, subCount))),
        format: DateFormats.dayOnly,
      },
      [DateRangeEnum.Year]: {
        getPointFunc: eachMonthOfInterval,
        findValueFunc: (data, baseDate, subCount) =>
          data.find((d) => isSameMonth(d.from, subMonths(baseDate, subCount))),
        format: DateFormats.shortMonth,
      },
    } satisfies Record<
      DateRangeEnum,
      {
        getPointFunc: (interval: Interval) => Date[];
        findValueFunc: (data: IAggregatedValue[], baseDate: Date, subCount: number) => IAggregatedValue | undefined;
        format: DateFormats;
      }
    >
  )[dateRange]);

export const changeRangeFunc = (dateRange: DateRangeEnum, param: 'next' | 'prev') =>
  ((
    {
      [DateRangeEnum.Week]: param === 'next' ? addWeeks : subWeeks,
      [DateRangeEnum.Month]: param === 'next' ? addMonths : subMonths,
      [DateRangeEnum.Year]: param === 'next' ? addYears : subYears,
    } satisfies Record<DateRangeEnum, (date: Date, amount: number) => Date>
  )[dateRange]);

export const convertIntervalToString = (dateRange: DateRangeEnum, interval: Interval): string =>
  ((
    {
      [DateRangeEnum.Week]: formatDateRange(new Date(interval.start), new Date(interval.end)),
      [DateRangeEnum.Month]: localFormat(new Date(interval.start), DateFormats.monthWithYear),
      [DateRangeEnum.Year]: localFormat(new Date(interval.start), DateFormats.yearOnly),
    } satisfies Record<DateRangeEnum, string>
  )[dateRange]);

export const minWidthForDateString = (dateRange: DateRangeEnum): number =>
  ((
    {
      [DateRangeEnum.Week]: 180,
      [DateRangeEnum.Month]: 120,
      [DateRangeEnum.Year]: 40,
    } satisfies Record<DateRangeEnum, number>
  )[dateRange]);

export const getIntervalFromDate = (date: Date, dateRange: DateRangeEnum): Interval =>
  ((
    {
      [DateRangeEnum.Week]: {
        start: startOfWeek(date, { weekStartsOn: 1 }),
        end: endOfWeek(date, { weekStartsOn: 1 }),
      },
      [DateRangeEnum.Month]: { start: startOfMonth(date), end: endOfMonth(date) },
      [DateRangeEnum.Year]: { start: startOfYear(date), end: endOfYear(date) },
    } satisfies Record<DateRangeEnum, Interval>
  )[dateRange]);

export const dateRangeToAggregationPeriod = (interval: DateRangeEnum): AggregationPeriodEnum => {
  if (interval === DateRangeEnum.Year) return AggregationPeriodEnum.Monthly;
  return AggregationPeriodEnum.Daily;
};

export const getTrendBarColor = (qualityMetric?: QualityMetric | null) => {
  if (!qualityMetric) qualityMetric = QualityMetric.Empty;

  return (
    {
      [QualityMetric.Empty]: 'transparent',
      [QualityMetric.Awful]: '#FE8966',
      [QualityMetric.Bad]: '#FCBA5E',
      [QualityMetric.Normal]: '#F5D252',
      [QualityMetric.Good]: '#AAE195',
      [QualityMetric.Awesome]: '#65C5AD',
      [QualityMetric.NotCalculated]: '#9BA7E6',
    } satisfies Record<QualityMetric, string>
  )[qualityMetric];
};

export const isInStudyInterval = (pointDate: Date, studyStartedAt: Date | null, studyFinishedAt: Date | null) => {
  const from = studyStartedAt;
  const to = studyFinishedAt ?? endOfDay(new Date());
  const isValidInterval = from && !isAfter(from, to);

  return (
    isValidInterval &&
    isWithinInterval(pointDate, {
      start: from,
      end: to,
    })
  );
};

export type BarType = 'OutOfStudy' | 'NoDevice' | 'WaitingForData' | 'ZeroData' | 'OkData';
export const getBarType = (
  date: Date | undefined,
  source: IAggregatedValue | null,
  deviceBindings: IDeviceBindingDto[] | undefined,
): BarType => {
  const outOfStudyRange = !date || !source;

  if (outOfStudyRange) return 'OutOfStudy';

  const waitingData = !!(
    source.value == null &&
    deviceBindings?.some((db) =>
      areIntervalsOverlapping(
        { start: db.assignedAt, end: db.unassignedAt ?? endOfDay(new Date()) },
        { start: date, end: date },
      ),
    )
  );
  if (waitingData) return 'WaitingForData';

  const noDevice = source.value == null && !waitingData;
  if (noDevice) return 'NoDevice';

  if (source.value === 0) return 'ZeroData';

  return 'OkData';
};

export const getBarAppearance = (
  barType: BarType,
  qualityMetric: QualityMetric | null | undefined,
): BarAppearanceType => {
  const obj = {
    OutOfStudy: {
      variant: 'Filled',
      color: getTrendBarColor(null),
    },
    WaitingForData: {
      variant: 'Dashed',
    },
    NoDevice: {
      variant: 'BottomBorder',
      color: '#7E8B8B',
    },
    ZeroData: {
      variant: 'BottomBorder',
      color: getTrendBarColor(qualityMetric),
    },
    OkData: {
      variant: 'Filled',
      color: getTrendBarColor(qualityMetric),
    },
  } satisfies Record<BarType, BarAppearanceType>;
  return obj[barType];
};

type ValueMapFunctionReturnType = (value: number | null | undefined) => number;
export const valueMapFunction = (deviceParameter: SummaryCalculatorEnum): ValueMapFunctionReturnType =>
  ((
    {
      [SummaryCalculatorEnum.Compliance]: (value: number | null | undefined) => (!value ? 0 : value / 60),
    } satisfies Record<SummaryCalculatorEnum, ValueMapFunctionReturnType>
  )[deviceParameter]);

export const getUnits = (deviceParameter: SummaryCalculatorEnum): string =>
  ((
    {
      [SummaryCalculatorEnum.Compliance]: i18next.t(
        'Dashboard.DeviceSummaryTrends.Units.Compliance',
        'Compliance_Units',
      ),
    } satisfies Record<SummaryCalculatorEnum, string>
  )[deviceParameter]);
